import { useCallback, useState } from 'react';
import type { CabinetFiscalPeriodModalProps } from '../components/CabinetFiscalPeriodModal';
import type { CabinetFiscalClient } from '../utils/cabinetFiscalApi';
import { isCabinetFiscalPeriodRequiredError } from '../utils/hubApiError';
import { __ } from '../i18n/hub';

export type CabinetMissionStartPrefill = {
  fiscal_year_start?: string;
  fiscal_year_end?: string;
};

export type UseCabinetMissionStartWithFiscalModalArgs<TPayload> = {
  fiscalClient: CabinetFiscalClient;
  startMission: () => Promise<TPayload>;
  onMissionStarted: (payload: TPayload) => void;
  onStartError?: (message: string) => void;
  prefillFromEntry?: CabinetMissionStartPrefill;
};

export type UseCabinetMissionStartWithFiscalModalResult = {
  runStartMission: () => Promise<void>;
  isStarting: boolean;
  fiscalModalProps: CabinetFiscalPeriodModalProps;
};

export const useCabinetMissionStartWithFiscalModal = <TPayload,>({
  fiscalClient,
  startMission,
  onMissionStarted,
  onStartError,
  prefillFromEntry,
}: UseCabinetMissionStartWithFiscalModalArgs<TPayload>): UseCabinetMissionStartWithFiscalModalResult => {
  const [modalOpen, setModalOpen] = useState(false);
  const [fiscalStart, setFiscalStart] = useState('');
  const [fiscalEnd, setFiscalEnd] = useState('');
  const [modalError, setModalError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [canEditCabinet, setCanEditCabinet] = useState(true);

  const loadFiscalFields = useCallback(async () => {
    try {
      const snapshot = await fiscalClient.fetchFiscal();
      setFiscalStart(snapshot.fiscal_year_start ?? prefillFromEntry?.fiscal_year_start ?? '');
      setFiscalEnd(snapshot.fiscal_year_end ?? prefillFromEntry?.fiscal_year_end ?? '');
      setCanEditCabinet(snapshot.can_edit_cabinet !== false);
    } catch {
      setFiscalStart(prefillFromEntry?.fiscal_year_start ?? '');
      setFiscalEnd(prefillFromEntry?.fiscal_year_end ?? '');
      setCanEditCabinet(true);
    }
  }, [fiscalClient, prefillFromEntry?.fiscal_year_end, prefillFromEntry?.fiscal_year_start]);

  const openModal = useCallback(async () => {
    setModalError(null);
    await loadFiscalFields();
    setModalOpen(true);
  }, [loadFiscalFields]);

  const runStartMission = useCallback(async () => {
    setIsStarting(true);
    try {
      const payload = await startMission();
      onMissionStarted(payload);
    } catch (error) {
      if (isCabinetFiscalPeriodRequiredError(error)) {
        await openModal();
        return;
      }

      const message = error instanceof Error
        ? error.message
        : __('Impossible de démarrer la mission.');
      onStartError?.(message);
    } finally {
      setIsStarting(false);
    }
  }, [onMissionStarted, onStartError, openModal, startMission]);

  const handleFiscalSave = useCallback(async () => {
    if (fiscalStart === '' || fiscalEnd === '') {
      setModalError(__('Veuillez renseigner les deux dates de l’exercice.'));
      return;
    }

    setIsSaving(true);
    setModalError(null);
    try {
      await fiscalClient.saveFiscal(fiscalStart, fiscalEnd);
      setModalOpen(false);
      await runStartMission();
    } catch (error) {
      setModalError(
        error instanceof Error ? error.message : __('Enregistrement impossible.'),
      );
    } finally {
      setIsSaving(false);
    }
  }, [fiscalClient, fiscalEnd, fiscalStart, runStartMission]);

  return {
    runStartMission,
    isStarting,
    fiscalModalProps: {
      isOpen: modalOpen,
      onClose: () => setModalOpen(false),
      fiscalYearStart: fiscalStart,
      fiscalYearEnd: fiscalEnd,
      onFiscalYearStartChange: setFiscalStart,
      onFiscalYearEndChange: setFiscalEnd,
      onSave: () => {
        void handleFiscalSave();
      },
      isSaving,
      error: modalError,
      canEdit: canEditCabinet,
    },
  };
};
